import React, {useMemo} from 'react';

import {floatPriceFromFormatted} from '@optimaxdev/utils';

import {mapKeys} from 'remeda';

import {PRODUCT_TYPES} from 'constants/product';

import {SUMMARY_TITLES} from 'desktop/pages/cart/constants/upsells';
import {CartItemGlassesTypeV2Type} from 'desktop/pages/cart/types';
import {QuoteItemV2OptionType} from 'desktop/pages/cart/types/quoteItem';
import {CheckoutItemOptionType, CheckoutItemType} from 'desktop/types/checkoutItemsType';
import {removeTags} from 'libs/removeTags/removeTags';

import {OrderDetailsItemsType} from 'types/orderDetailsTypes';

import {BaseItemPrice} from '../baseItemPrice/baseItemPrice';

import {getPackageBasicData} from './packageItems/packageItems';

import s from './cartItemHelper.scss';

const createPriceElement = (option: QuoteItemV2OptionType) => (
    <span className={s.price}>
        (
        {option.price > 0 ? (
            <BaseItemPrice
                price={floatPriceFromFormatted(option.price)}
                specialPrice={option.finalPrice}
            />
        ) : (
            'Free'
        )}
        )
    </span>
);

/**
 * Creates eyeglasses text elements at the bottom of Lens option
 */
const createEyeglassesDescription = (
    item: CheckoutItemType | CartItemGlassesTypeV2Type | OrderDetailsItemsType,
) => {
    const {
        light_adjusting: lightAdjusting,
        package: packageOption,
        sunglasses_options: sunglassesOptions,
    } = mapKeys(
        item.options as {
            [key: string]: CheckoutItemOptionType;
        },
        (_, value) => value.option.alias,
    );

    const packageOptionBasicData = getPackageBasicData({
        description: removeTags(
            packageOption.packageDetails?.map(detail => detail.title).join(', ') || '',
        ),
        isEyeglasses: true,
        options: item.options as {
            [key: string]: CheckoutItemOptionType;
        },
        title: packageOption.title,
    });

    return (
        <p className={s.description}>
            {packageOptionBasicData.title} package: {packageOptionBasicData.description}&nbsp;
            {createPriceElement(packageOption)}
            {lightAdjusting && (
                <>
                    ,{' '}
                    {lightAdjusting.sku in SUMMARY_TITLES
                        ? SUMMARY_TITLES[lightAdjusting.sku as keyof typeof SUMMARY_TITLES]
                        : lightAdjusting.title}{' '}
                    {createPriceElement(lightAdjusting)}
                </>
            )}
            {sunglassesOptions && (
                <>
                    , {sunglassesOptions.title} {createPriceElement(sunglassesOptions)}
                </>
            )}
        </p>
    );
};

/**
 * Creates sunglasses text elements at the bottom of Lens option
 */
const createSunglassesDescription = (
    item: CheckoutItemType | CartItemGlassesTypeV2Type | OrderDetailsItemsType,
) => {
    const {
        color,
        darkness,
        light_adjusting: lightAdjusting,
        thickness,
    } = mapKeys(
        item.options as {
            [key: string]: CheckoutItemOptionType;
        },
        (_, value) => value.option.alias,
    );

    /**
     * We added new lenses (Transitions Drivewear) for SG - it has the same alias as EG - light_adjusting
     */
    const activeSgColor = color || lightAdjusting;
    return (
        <>
            <p className={s.description}>
                {thickness.title} package:{' '}
                {removeTags(thickness.packageDetails?.map(detail => detail.title).join(', ') || '')}{' '}
                {createPriceElement(thickness)}
            </p>
            <p>
                Lens color: {activeSgColor.title} {createPriceElement(activeSgColor)}
                {darkness && (
                    <>
                        {', '}
                        {Number(darkness.title)
                            ? `${darkness.title}% ${darkness.option.title}`
                            : `${darkness.title} ${darkness.option.title}`}{' '}
                        {createPriceElement(darkness)}
                    </>
                )}
            </p>
        </>
    );
};

/**
 * hook for getting item usage option
 */
export const useUsage = (
    item: CheckoutItemType | CartItemGlassesTypeV2Type | OrderDetailsItemsType,
) => {
    return useMemo(
        () =>
            Object.values(item.options).find(
                addedOption => addedOption.option && addedOption.option.alias === 'usage',
            ),
        [item.options],
    );
};

/**
 * hook for getting item prism option
 */
export const usePrismContent = (item: CheckoutItemType | CartItemGlassesTypeV2Type) => {
    return useMemo(() => {
        const prism = Object.values(item.options).find(
            addedOption => addedOption.option && addedOption.option.alias === 'prism',
        );

        return (
            prism && (
                <>
                    {', '}
                    {prism.title} {createPriceElement(prism)}
                </>
            )
        );
    }, [item]);
};

/**
 * hook for getting item description
 */
export const useDescription = (
    item: CheckoutItemType | CartItemGlassesTypeV2Type | OrderDetailsItemsType,
    productType: string,
) => {
    return useMemo(() => {
        // Unsupported on backend migration from wizard v1 to wizard v2
        // If item contains wizard options from version 1 on backend side
        // the options object will be empty (it transforms from object to array due to parse of JSON s)
        if (Array.isArray(item.options) && item.options.length === 0) return null;

        return productType === PRODUCT_TYPES.SUNGLASSES
            ? !Array.isArray(item.options) && createSunglassesDescription(item)
            : createEyeglassesDescription(item);
    }, [item, productType]);
};
